import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlignJustify } from "lucide-react";
import { UserMenu } from "./UserMenu";
import { Badge, BadgeProps, styled } from "@mui/material";
import logo from "../../images/ImprovedFundraiserPage/MaMF.svg";
import cart from "../../images/ImprovedFundraiserPage/cart.svg";
import "../../pages/ImprovedShopPage/styles/custom.css";
import "./Navbar.css";
import "../../components/Common/Banner.css";
import config from "../../config";
import { useCart } from "../../contexts/Cart/CartContext";

function Navbar() {
  const [offCanvasOpen, setOffCanvasOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { toggleDrawer, totalItems } = useCart();

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");
    setIsLoggedIn(!!accessToken);
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    setIsLoggedIn(false);
    handleMenuClose();
    window.location.href = "/login";
  };

  // Animation variants for menu items
  const menuVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const bannerMessage = config.BANNER_MESSAGE;
  const bannerHeight = bannerMessage ? "40px" : "0";

  const navItems = [
    { label: "Home Page", path: "/" },
    { label: "About Us", path: "/about-us" },
    { label: "Contact Us", path: "/contact-us" },
    { label: "FAQ", path: "/faq" },
  ];

  const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
    "& .MuiBadge-badge": {
      right: 10,
      top: 10,
      padding: 0,
      scale: 0.8,
    },
  }));

  return (
    <div>
      <div
        className="nav-container"
        style={{ "--banner-height": bannerHeight } as React.CSSProperties}
      >
        <nav className="nav">
          <div className="nav-content sec-wrapper">
            <div className="logo">
              <img src={logo} alt="Logo" className="logo-img" />
            </div>

            <ul className="nav-list">
              {navItems.map((item) => (
                <li key={item.path}>
                  <a href={item.path} className="nav-link">
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>

            <div className="d-flex align-items-center gap-3">
              <button
                className="cart-button"
                onClick={toggleDrawer}
                aria-label="Shopping cart"
              >
                <StyledBadge badgeContent={totalItems} color="error">
                  <img src={cart} alt="Cart" className="h-10" />
                </StyledBadge>
              </button>

              <div className="action-buttons">
                <a
                  href="/new-fundraiser-request"
                  className="action-button btn-signup"
                >
                  Start Fundraiser
                </a>
                <a
                  href="/register-or-shop"
                  className="action-button btn-signup"
                >
                  Join Fundraiser
                </a>
                <UserMenu isLoggedIn={isLoggedIn} onLogout={handleLogout} />
              </div>

              <AlignJustify
                className="mobile-menu-button"
                onClick={() => setOffCanvasOpen(true)}
              />
            </div>
          </div>
        </nav>

        {/* Banner at the bottom of the navbar */}
        {bannerMessage && (
          <div className="global-banner" style={{ height: bannerHeight }}>
            <div className="banner-content">
              <p className="banner-text mb-0">{bannerMessage}</p>
            </div>
          </div>
        )}
      </div>

      {/* Off-canvas menu for mobile with animation */}
      <AnimatePresence>
        {offCanvasOpen && (
          <motion.div
            key="mobile-menu"
            className="position-fixed top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-start d-lg-none py-5 footer-bg z-mobile-menu"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <button
              className="position-absolute top-0 end-0 text-white fs-1 border-0 bg-transparent mt-3 me-3"
              onClick={() => setOffCanvasOpen(false)}
              aria-label="Close menu"
            >
              &times;
            </button>

            {/* Animated menu list */}
            <motion.ul
              className="d-flex flex-column gap-4 fs-2 fw-semibold text-center text-white text-uppercase list-unstyled"
              variants={menuVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
            >
              {[
                { text: "Home Page", path: "/" },
                { text: "About Us", path: "/about-us" },
                { text: "Contact Us", path: "/contact-us" },
                { text: "FAQ", path: "/faq" },
                isLoggedIn
                  ? { text: "Dashboard", path: "/dashboard" }
                  : { text: "Log in", path: "/login" },
                ...(isLoggedIn
                  ? [
                      {
                        text: "Logout",
                        path: "#",
                        onClick: handleLogout,
                      },
                    ]
                  : []),
                {
                  text: "Start Fundraiser",
                  path: "/new-fundraiser-request",
                  className: "mt-3",
                },
                {
                  text: "Join Fundraiser",
                  path: "/register-or-shop",
                  className: "mb-3",
                },
              ].map((item, idx) => (
                <motion.li
                  key={idx}
                  variants={itemVariants}
                  onClick={() => setOffCanvasOpen(false)}
                  className={`cursor-pointer ${item.className || ""}`}
                >
                  <a
                    href={item.path}
                    className="text-decoration-none text-white"
                    onClick={item.onClick ? item.onClick : undefined}
                  >
                    {item.text}
                  </a>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default Navbar;
